/**
 * Mapper plugin adapter contracts (L0–L5).
 * Each adapter normalizes an external source to a common interface.
 */

import { cpFetch } from '@/lib/cpClient';
import { apiFetch } from '@/lib/http';
import { MAPPER_BASEMAP_STYLE } from '../mapBasemapStyle';
import { MAPPER_CADASTRAL_PMTILES_PATH } from '../constants';
import type { MapperPluginConfig } from '../mapperConfig';

/** L0 — Basemap */
export const BasemapAdapter = {
  getStyle() {
    return MAPPER_BASEMAP_STYLE;
  },
  listOptions() {
    return ['esri-world-imagery', 'osm-street', 'terrain'];
  },
};

/** L1 — Cadastral reference */
export const ReferenceVectorAdapter = {
  getSourceSpec(origin: string) {
    return {
      pmtilesPath: `${origin}${MAPPER_CADASTRAL_PMTILES_PATH}`,
      sourceLayer: 'parcels',
    };
  },
  async query(params: Record<string, string>) {
    const qs = new URLSearchParams(params).toString();
    const res = await cpFetch(`/mapper/parcels?${qs}`);
    return res.json();
  },
  async byPoint(lat: number, lng: number) {
    const res = await cpFetch(`/mapper/parcels/by-point?lat=${lat}&lng=${lng}`);
    return res.json();
  },
};

/** L2 — Land units */
export const GeometrySource = {
  async list() {
    const res = await cpFetch('/mapper/land-units');
    return res.json();
  },
  async get(id: string) {
    const res = await cpFetch(`/mapper/land-units/${encodeURIComponent(id)}`);
    return res.json();
  },
  async create(body: Record<string, unknown>) {
    const res = await cpFetch('/mapper/land-units', { method: 'POST', body: JSON.stringify(body) });
    return res.json();
  },
  async update(id: string, body: Record<string, unknown>) {
    const res = await cpFetch(`/mapper/land-units/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return res.json();
  },
  async delete(id: string) {
    const res = await cpFetch(`/mapper/land-units/${encodeURIComponent(id)}`, { method: 'DELETE' });
    return res.json();
  },
};

/** L3 — IKU-typed objects (live template_registry) */
export const ObjectSource = {
  async palette(config: MapperPluginConfig) {
    const ids = config.layers.objects.palette?.filter(Boolean) || [];
    const qs = ids.length
      ? `?template_ids=${encodeURIComponent(ids.join(','))}`
      : '?iku_prefix=iku:infrastructure';
    const res = await cpFetch(`/mapper/registry/templates${qs}`);
    return res.json();
  },
  async attributeSchema(templateId: string) {
    const res = await cpFetch(`/mapper/registry/templates/${encodeURIComponent(templateId)}`);
    return res.json();
  },
  async list(params: Record<string, string> = {}) {
    const qs = new URLSearchParams(params).toString();
    const res = await cpFetch(`/mapper/infrastructure${qs ? `?${qs}` : ''}`);
    return res.json();
  },
};

/** L4 — Live positions (poll telemetry-read via Kong /api/telemetry) */
export const LiveFeedAdapter = {
  async latest(deviceIds: string[]) {
    const results = await Promise.all(
      deviceIds.map(async (deviceId) => {
        try {
          return await apiFetch(
            `/telemetry/latest?deviceId=${encodeURIComponent(deviceId)}&limit=1`,
          );
        } catch {
          return null;
        }
      }),
    );
    return results.filter(Boolean);
  },
  async trail(deviceId: string, window = '24h') {
    try {
      return await apiFetch(
        `/telemetry/series?deviceId=${encodeURIComponent(deviceId)}&window=${encodeURIComponent(window)}`,
      );
    } catch {
      return null;
    }
  },
  subscribe(pollMs: number, callback: () => void, getPaused: () => boolean) {
    const id = window.setInterval(() => {
      if (document.hidden || getPaused()) return;
      callback();
    }, pollMs);
    return () => window.clearInterval(id);
  },
};

/** L5 — Raster overlays (ISRIC WMS) */
export const RasterOverlayAdapter = {
  isricWmsUrl(property: string, use3857 = true) {
    const crs = use3857 ? '&CRS=EPSG:3857' : '';
    return `https://maps.isric.org/mapserv?map=/map/${property}.map&SERVICE=WMS&REQUEST=GetMap&FORMAT=image/png&TRANSPARENT=TRUE${crs}`;
  },
  async checkIsricCapabilities() {
    const res = await cpFetch('/mapper/overlays/isric/capabilities');
    return res.json();
  },
};

/** L5 — Point query (best-effort) */
export const PointQueryAdapter = {
  async query(dataset: string, lat: number, lng: number) {
    const res = await cpFetch(
      `/mapper/overlays/point-query?dataset=${encodeURIComponent(dataset)}&lat=${lat}&lng=${lng}`,
    );
    return res.json();
  },
};
